import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress,
  Alert,
  Paper,
  styled,
  TextField,
  Grid,
  Divider,
  Step,
  StepLabel,
  Stepper,
  AlertTitle,
  InputAdornment
} from '@mui/material';
import { getMarinaSettings } from '../firebase/services/marinaSettingsService';
import { 
  createPayment as createSquarePayment, 
  createCustomer as createSquareCustomerService,
  findCustomerByEmail
} from '../firebase/services/squareService';
import { validatePromoCode, applyPromoCode } from '../firebase/services/discountService';
import { getSquareConfig } from '../utils/environment';
import { Check, InfoOutlined } from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';

// Payment processor icons
import { 
  Apps as SquareIcon
} from '@mui/icons-material';

// Styled components
const PaymentMethodTab = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start'
}));

// Generate a unique ID for the card container
const SQUARE_CARD_CONTAINER_ID = "square-card-container-unique";

interface MultiPaymentFormProps {
  amount: number;
  paymentType?: string;
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    vesselType?: string;
    date?: string;
    startTime?: string;
    endTime?: string;
  };
  onPaymentComplete: (paymentId: string, status: string, processor: string) => void;
  onPaymentError: (error: string) => void;
  disabled?: boolean;
}

const MultiPaymentForm: React.FC<MultiPaymentFormProps> = ({
  amount,
  paymentType,
  customerInfo,
  onPaymentComplete,
  onPaymentError,
  disabled = false
}) => {
  console.log('MultiPaymentForm mounted with props:', {
    amount,
    paymentType,
    hasCustomerInfo: !!customerInfo,
    displayAmount: amount
  });

  const [paymentAmount, setPaymentAmount] = useState<number>(amount);
  const [displayAmount, setDisplayAmount] = useState<number>(amount);
  const [isDepositPayment, setIsDepositPayment] = useState<boolean>(false);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<any>(null);
  const [hasSettings, setHasSettings] = useState<boolean>(false);
  const [squareLoaded, setSquareLoaded] = useState<boolean>(false);
  const [squarePaymentForm, setSquarePaymentForm] = useState<any>(null);
  const squareInitialized = useRef<boolean>(false);
  
  // Add state for reservation data
  const [reservationData, setReservationData] = useState<any>(null);

  // Add promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDetails, setPromoCodeDetails] = useState<any>(null);
  const [checkingPromoCode, setCheckingPromoCode] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);

  // Add state for promo code box visibility
  const [showPromoBox, setShowPromoBox] = useState(false);

  console.log('MultiPaymentForm - Amount passed as prop:', amount);
  
  // Helper function to round to nearest quarter
  const roundToNearestQuarter = (amount: number): number => {
    return Math.round(amount * 4) / 4;
  };

  // Helper to get deposit amount
  const getDepositAmount = (settings: any, reservationData: any) => {
    if (!settings?.reservationRules?.depositRequired || !reservationData) {
      return null;
    }

    // Always use the same deposit calculation logic consistently
    const depositAmount = settings.reservationRules.depositType === 'fixed' ?
      settings.reservationRules.depositAmount :
      roundToNearestQuarter(reservationData.finalPrice * (settings.reservationRules.depositAmount / 100));
    
    return depositAmount;
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      let date;
      // If format is YYYY-MM-DD, parse as local time
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'N/A';
    }
  };

  // Function to sync with localStorage - reused in multiple places
  const syncWithLocalStorage = () => {
    try {
      const reservationDataStr = localStorage.getItem('reservationData');
      if (reservationDataStr) {
        const reservationData = JSON.parse(reservationDataStr);
        console.log('Syncing with localStorage, found data:', reservationData);
        
        // Set the reservation data state
        setReservationData(reservationData);
        
        // Set the total price from localStorage
        const finalPrice = reservationData.finalPrice || 0;
        console.log('Updated from localStorage, finalPrice:', finalPrice, 'Type:', typeof finalPrice, 'Is zero?', finalPrice === 0);
        setTotalPrice(finalPrice);
        
        // Check if promo code was already applied
        if (reservationData.promoCode && reservationData.discountAmount > 0) {
          setPromoCode(reservationData.promoCode);
          setPromoCodeApplied(true);
          setDiscountAmount(reservationData.discountAmount);
          setOriginalPrice(reservationData.originalPrice);
        }
        
        // Calculate the correct deposit amount if we have settings
        if (settings?.reservationRules?.depositRequired) {
          const depositAmount = getDepositAmount(settings, reservationData);
          if (depositAmount !== null) {
            setDisplayAmount(depositAmount);
            setPaymentAmount(depositAmount);
            setIsDepositPayment(true);
          }
        } else {
          setDisplayAmount(finalPrice);
          setPaymentAmount(finalPrice);
        }
      }
    } catch (err) {
      console.error('Error syncing with localStorage:', err);
    }
  };

  // Promo code validation function
  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError('Please enter a promo code');
      return;
    }
    
    if (!reservationData?.date) {
      setPromoCodeError('Reservation data is missing');
      return;
    }
    
    setCheckingPromoCode(true);
    setPromoCodeError(null);
    setPromoCodeApplied(false);
    
    try {
      console.log('Validating promo code:', promoCode);
      
      // Get current pricing from reservation data
      const currentRentalPrice = reservationData.rentalPrice || reservationData.originalPrice || 0;
      const vesselType = reservationData.vesselType || 'pontoon';
      const bookingDate = new Date(reservationData.date);
      
      // Call the discount service to validate the promo code
      const result = await validatePromoCode(
        promoCode,
        'guest', // Use guest for now, could be improved with actual user ID
        currentRentalPrice,
        vesselType,
        bookingDate
      );
      
      console.log('Promo code validation result:', result);
      
      if (!result) {
        setPromoCodeError('Invalid or expired promo code');
        setDiscountAmount(0);
        return;
      }
      
      // Store the discount information
      setPromoCodeDetails(result.discountDetails);
      setPromoCodeApplied(true);
      
      // Save original price if not already set
      if (originalPrice === 0) {
        setOriginalPrice(currentRentalPrice);
      }
      
      // Apply the discount
      const discountAmt = result.discount;
      setDiscountAmount(discountAmt);
      
      // Calculate the new rental price after discount
      const newRentalPrice = Math.max(0, currentRentalPrice - discountAmt);
      console.log(`Applying discount of $${discountAmt} to rental price $${currentRentalPrice}, new price: $${newRentalPrice}`);
      
      // Calculate the final price including damage waiver and tax
      const waiverCost = reservationData.damageWaiverAmount || 0;
      const subtotal = newRentalPrice + waiverCost;
      const taxRate = settings?.reservationRules?.salesTaxRate ? (settings.reservationRules.salesTaxRate / 100) : 0.0825;
      const taxAmount = subtotal * taxRate;
      const finalPrice = subtotal + taxAmount;
      
      // Update reservation data with discount
      const updatedReservationData = {
        ...reservationData,
        rentalPrice: newRentalPrice,
        originalPrice: originalPrice || currentRentalPrice,
        discountAmount: discountAmt,
        promoCode: promoCode,
        promoCodeDetails: result.discountDetails,
        taxAmount: taxAmount,
        finalPrice: finalPrice
      };
      
      // Update localStorage
      localStorage.setItem('reservationData', JSON.stringify(updatedReservationData));
      
      // Update local state
      setReservationData(updatedReservationData);
      setTotalPrice(finalPrice);
      
      // Update display amount for payment
      if (settings?.reservationRules?.depositRequired) {
        const depositAmount = getDepositAmount(settings, updatedReservationData);
        if (depositAmount !== null) {
          setDisplayAmount(depositAmount);
          setPaymentAmount(depositAmount);
        }
      } else {
        setDisplayAmount(finalPrice);
        setPaymentAmount(finalPrice);
      }
      
      // Trigger a storage event so other components can refresh
      window.dispatchEvent(new Event('storage'));
      
      // Clear the error
      setPromoCodeError(null);
    } catch (err) {
      console.error('Error validating promo code:', err);
      setPromoCodeError('Failed to validate promo code');
      setDiscountAmount(0);
    } finally {
      setCheckingPromoCode(false);
    }
  };

  // Clear promo code application
  const handleClearPromoCode = () => {
    setPromoCode('');
    setPromoCodeApplied(false);
    setPromoCodeDetails(null);
    setPromoCodeError(null);
    
    // Restore original price if discount was applied
    if (discountAmount > 0 && originalPrice > 0) {
      const waiverCost = reservationData.damageWaiverAmount || 0;
      const subtotal = originalPrice + waiverCost;
      const taxRate = settings?.reservationRules?.salesTaxRate ? (settings.reservationRules.salesTaxRate / 100) : 0.0825;
      const taxAmount = subtotal * taxRate;
      const finalPrice = subtotal + taxAmount;
      
      // Update reservation data without promo code
      const updatedReservationData = {
        ...reservationData,
        rentalPrice: originalPrice,
        originalPrice: originalPrice,
        discountAmount: 0,
        promoCode: null,
        promoCodeDetails: null,
        taxAmount: taxAmount,
        finalPrice: finalPrice
      };
      
      // Update localStorage
      localStorage.setItem('reservationData', JSON.stringify(updatedReservationData));
      
      // Update local state
      setReservationData(updatedReservationData);
      setTotalPrice(finalPrice);
      setDiscountAmount(0);
      setOriginalPrice(0);
      
      // Update display amount for payment
      if (settings?.reservationRules?.depositRequired) {
        const depositAmount = getDepositAmount(settings, updatedReservationData);
        if (depositAmount !== null) {
          setDisplayAmount(depositAmount);
          setPaymentAmount(depositAmount);
        }
      } else {
        setDisplayAmount(finalPrice);
        setPaymentAmount(finalPrice);
      }
      
      // Trigger a storage event so other components can refresh
      window.dispatchEvent(new Event('storage'));
    }
  };

  // Load reservation data from localStorage
  useEffect(() => {
    syncWithLocalStorage();
    
    // Listen for storage changes
    const handleStorageChange = () => {
      syncWithLocalStorage();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const formatTime = (timeString: string): string => {
    if (!timeString) return '';
    
    // If it's already in 12-hour format, return as is
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    // Convert from 24-hour format to 12-hour format
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const parseTimeSlot = (timeSlot: string) => {
    if (!timeSlot) return { startTime: '', endTime: '', duration: '' };
    
    if (timeSlot.includes('-')) {
      const [start, end] = timeSlot.split('-');
      const startTime = formatTime(start.trim());
      const endTime = formatTime(end.trim());
      
      // Calculate duration
      const startDate = new Date(`2000-01-01 ${start.trim()}`);
      const endDate = new Date(`2000-01-01 ${end.trim()}`);
      const durationMs = endDate.getTime() - startDate.getTime();
      const durationHours = durationMs / (1000 * 60 * 60);
      
      return {
        startTime,
        endTime,
        duration: `${durationHours} ${durationHours === 1 ? 'hour' : 'hours'}`
      };
    }
    
    return { startTime: timeSlot, endTime: '', duration: '1 hour' };
  };

  // Initial fetch of settings and sync with localStorage
  useEffect(() => {
    fetchSettings();
    syncWithLocalStorage();
  }, []);

  // Modify the useEffect for localStorage sync to prevent flickering
  useEffect(() => {
    // Initial sync with localStorage
    try {
      const reservationDataStr = localStorage.getItem('reservationData');
      if (reservationDataStr) {
        const reservationData = JSON.parse(reservationDataStr);
        console.log('Initial sync with localStorage, found data:', reservationData);
        
        // Set the total price from localStorage
        const finalPrice = reservationData.finalPrice || 0;
        console.log('Initial price set from localStorage, finalPrice:', finalPrice);
        setTotalPrice(finalPrice);
        
        // Calculate the correct deposit amount if we have settings
        if (settings?.reservationRules?.depositRequired) {
          const depositAmount = settings.reservationRules.depositType === 'fixed' ?
            settings.reservationRules.depositAmount :
            roundToNearestQuarter(finalPrice * (settings.reservationRules.depositAmount / 100));
          
          console.log('Setting fixed deposit amount:', depositAmount);
          setDisplayAmount(depositAmount);
          setPaymentAmount(depositAmount);
          setIsDepositPayment(true);
        } else {
          setDisplayAmount(finalPrice);
          setPaymentAmount(finalPrice);
        }
      }
    } catch (err) {
      console.error('Error in initial localStorage sync:', err);
    }
  }, [settings]); // Only run when settings change, not on every render

  // Fetch marina settings
  const fetchSettings = async () => {
    try {
      const settings = await getMarinaSettings();
      console.log('Fetched Marina Settings:', settings);
      console.log('Reservation Rules:', settings.reservationRules);
      console.log('Deposit Required:', settings.reservationRules?.depositRequired);
      console.log('Current displayAmount in component:', displayAmount);

      setSettings(settings);

      // Get reservation data from localStorage
      const reservationDataStr = localStorage.getItem('reservationData');
      if (reservationDataStr) {
        const reservationData = JSON.parse(reservationDataStr);
        console.log('Reservation data from localStorage:', reservationData);
        
        // Set the reservation data state
        setReservationData(reservationData);

        if (settings.reservationRules?.depositRequired) {
          const depositAmount = getDepositAmount(settings, reservationData);
          if (depositAmount !== null) {
            console.log('Deposit required and found in localStorage:', depositAmount);
            setDisplayAmount(depositAmount);
            setPaymentAmount(depositAmount);
            setIsDepositPayment(true);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching marina settings:', error);
      // Use fallback credentials on error
      const fallbackConfig = getSquareConfig();
      setSettings({
        ...fallbackConfig,
        activePaymentMethods: { square: true },
        reservationRules: {
          minReservationDuration: 2,
          maxReservationDuration: 8,
          advanceBookingDays: 30,
          cancellationPolicy: '',
          depositRequired: false,
          depositType: 'fixed',
          depositAmount: 0,
          baseRate: 0,
          salesTaxRate: 0
        }
      });
    }
  };

  // Square integration
  useEffect(() => {
    console.log('Square integration effect running:', {
      hasSettings: !!settings,
      squareLoaded,
      squareInitialized: squareInitialized.current,
      settings: settings ? {
        hasAppId: !!settings.squareApplicationId,
        hasLocationId: !!settings.squareLocationId,
        hasAccessToken: !!settings.squareAccessToken,
        environment: settings.squareEnvironment,
        activePaymentMethods: settings.activePaymentMethods
      } : null
    });

    if (!settings) {
      console.log('Settings not loaded yet, skipping Square SDK load');
      return;
    }

    // First ensure the container exists
    const container = document.getElementById(SQUARE_CARD_CONTAINER_ID);
    console.log('Square container check:', {
      containerExists: !!container,
      containerId: SQUARE_CARD_CONTAINER_ID,
      containerHTML: container ? container.innerHTML : null
    });

    if (!container) {
      console.error('Square card container not found');
      return;
    }
    
    const loadSquare = async () => {
      try {
        // Check if Square SDK is already loaded
        if (window.Square) {
          console.log('Square SDK already loaded');
          setSquareLoaded(true);
          return;
        }

        // Get environment from settings
        const environment = settings.squareEnvironment || 'production';
        
        console.log('Loading Square SDK...', {
          environment,
          hasAppId: !!settings.squareApplicationId,
          hasLocationId: !!settings.squareLocationId,
          containerExists: !!container,
          appId: settings.squareApplicationId,
          locationId: settings.squareLocationId,
          scriptSrc: environment === 'production' 
            ? 'https://web.squarecdn.com/v1/square.js'
            : 'https://sandbox.web.squarecdn.com/v1/square.js'
        });
        
        // Load Square SDK based on environment
        const script = document.createElement('script');
        script.src = environment === 'production' 
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';
        
        // Create a promise to handle script loading
        const loadPromise = new Promise((resolve, reject) => {
          script.onload = () => {
            console.log('Square SDK loaded successfully');
            setSquareLoaded(true);
            resolve(true);
          };
          
          script.onerror = (error) => {
            console.error('Error loading Square SDK:', error);
            reject(new Error('Failed to load Square payment system. Please try again later.'));
          };
        });
        
        // Add script to document
        document.body.appendChild(script);
        
        // Wait for script to load with timeout and retries
        let retries = 3;
        while (retries > 0) {
          try {
            await Promise.race([
              loadPromise,
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Square SDK load timeout')), 5000)
              )
            ]);
            break;
          } catch (err) {
            console.error(`Square SDK load attempt failed (${retries} retries left):`, err);
            retries--;
            if (retries === 0) {
              throw err;
            }
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      } catch (err) {
        console.error('Error loading Square SDK:', err);
        setError('Failed to load Square payment system. Please try again later.');
      }
    };

    loadSquare();
  }, [settings, squareLoaded, squareInitialized.current]);

  // Initialize Square - Ensure it only runs once
  useEffect(() => {
    console.log('Square initialization effect running:', {
      squareLoaded,
      hasSquare: !!window.Square,
      hasSettings: !!settings,
      isInitialized: squareInitialized.current,
      settings: settings ? {
        hasAppId: !!settings.squareApplicationId,
        hasLocationId: !!settings.squareLocationId,
        hasAccessToken: !!settings.squareAccessToken,
        environment: settings.squareEnvironment,
        activePaymentMethods: settings.activePaymentMethods
      } : null
    });

    if (!squareLoaded || !window.Square || !settings || squareInitialized.current) {
      console.log('Skipping Square initialization:', {
        squareLoaded,
        hasSquare: !!window.Square,
        hasSettings: !!settings,
        isInitialized: squareInitialized.current,
        reason: !squareLoaded ? 'SDK not loaded' : 
                !window.Square ? 'Square not in window' :
                !settings ? 'No settings' :
                squareInitialized.current ? 'Already initialized' : 'Unknown'
      });
      return;
    }

    const initializeSquare = async () => {
      try {
        // Clear any existing container first
        const existingContainer = document.getElementById(SQUARE_CARD_CONTAINER_ID);
        if (existingContainer) {
          console.log('Clearing existing Square container');
          existingContainer.innerHTML = '';
        }

        // Get the application ID and location ID from settings or fallback
        const applicationId = settings.squareApplicationId || getSquareConfig().applicationId;
        const locationId = settings.squareLocationId || getSquareConfig().locationId;
        const environment = settings.squareEnvironment || getSquareConfig().environment;

        console.log('Initializing Square with credentials:', {
          applicationId,
          locationId,
          environment,
          containerExists: !!existingContainer,
          containerHTML: existingContainer ? existingContainer.innerHTML : null
        });

        if (!applicationId || !locationId) {
          throw new Error('Square credentials are missing');
        }

        console.log('Creating Square payments instance');
        const payments = window.Square.payments(applicationId, locationId);
        
        console.log('Creating Square card instance');
        const card = await payments.card();
        
        console.log('Attaching card to container:', {
          containerId: SQUARE_CARD_CONTAINER_ID,
          containerExists: !!existingContainer
        });
        await card.attach(`#${SQUARE_CARD_CONTAINER_ID}`);
        
        setSquarePaymentForm(card);
        squareInitialized.current = true;
        console.log('Square payment form initialized successfully');
      } catch (err) {
        console.error('Error initializing Square:', err);
        setError('Failed to initialize Square payment form. Please try again later.');
      }
    };

    initializeSquare();

    // Cleanup function to detach card when component unmounts
    return () => {
      if (squarePaymentForm) {
        try {
          console.log('Cleaning up Square payment form');
          squarePaymentForm.destroy();
        } catch (e) {
          console.error('Error destroying Square form:', e);
        }
      }
    };
  }, [squareLoaded, settings, squareInitialized.current, squarePaymentForm]);

  // Create Square customer
  const createSquareCustomer = async () => {
    try {
      const { firstName, lastName, email, phone } = customerInfo;
      
      // First check if customer already exists in Square
      console.log('Checking if customer already exists with email:', email);
      const existingCustomerId = await findCustomerByEmail(email);
      
      if (existingCustomerId) {
        console.log('Found existing customer with ID:', existingCustomerId);
        return existingCustomerId;
      }
      
      // No existing customer found, create a new one
      console.log('No existing customer found, creating new customer');
      const response = await createSquareCustomerService(firstName, lastName, email, phone);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to create customer record');
      }
      
      console.log('Created new customer with ID:', response.customerId);
      return response.customerId;
    } catch (err) {
      console.error('Error handling Square customer:', err);
      throw new Error('Failed to create customer record. Please try again.');
    }
  };

  // Handle Square payment submission
  const handleSquarePayment = async () => {
    if (!squarePaymentForm && displayAmount > 0.01) {
      setError('Payment system is not fully loaded. Please try again in a moment.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the displayAmount which is set properly from settings, rather than reading again from localStorage
      const finalPaymentAmount = displayAmount;
      const isDeposit = isDepositPayment;
      
      // Log payment details for debugging
      console.log('PAYMENT DETAILS:', {
        displayAmount,
        finalPaymentAmount,
        isDepositPayment,
        totalPrice
      });

      // If the payment amount is zero or very close to zero (100% discount)
      if (finalPaymentAmount === 0 || finalPaymentAmount < 0.01) {
        console.log('Payment amount is zero, completing without charging');
        
        // If a promo code was used, increment its usage count
        if (promoCodeApplied && promoCodeDetails?.id) {
          try {
            await applyPromoCode(promoCodeDetails.id);
            console.log(`Promo code ${promoCode} applied successfully for zero-dollar payment`);
          } catch (err) {
            console.error('Error applying promo code for zero-dollar payment:', err);
            // Don't stop payment process if promo code update fails
          }
        }
        
        // Add a small delay to show processing state
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        onPaymentComplete(
          'free-' + Date.now(), 
          'COMPLETED', 
          'none'
        );
        return;
      }

      // Tokenize card
      const result = await squarePaymentForm.tokenize();
      
      if (result.status !== 'OK') {
        // Extract more user-friendly error message
        let errorMessage = 'Invalid card information';
        if (result.errors && result.errors.length > 0) {
          const fieldMap: any = {
            'cardNumber': 'card number',
            'expirationDate': 'expiration date',
            'cvv': 'security code (CVV)'
          };
          
          const firstError = result.errors[0];
          if (firstError.field) {
            const friendlyFieldName = fieldMap[firstError.field] || firstError.field;
            errorMessage = `Invalid ${friendlyFieldName}: ${firstError.message}`;
          } else {
            errorMessage = firstError.message;
          }
        }
        
        throw new Error(errorMessage);
      }
      
      try {
        // Create a customer
        const customerId = await createSquareCustomer();
        console.log('Square customer created with ID:', customerId);
        
        // Process the payment with the correct amount
        const response = await createSquarePayment(
          result.token,
          finalPaymentAmount,
          'USD',
          'Marina reservation', // Default description
          customerInfo // Pass customer info to the payment
        );
        
        if (!response.success) {
          throw new Error(response.error || 'Payment processing failed');
        }
        
        console.log('Payment succeeded:', response);
        
        // If a promo code was used, increment its usage count
        if (promoCodeApplied && promoCodeDetails?.id) {
          try {
            await applyPromoCode(promoCodeDetails.id);
            console.log(`Promo code ${promoCode} applied successfully`);
          } catch (err) {
            console.error('Error applying promo code:', err);
            // Don't stop payment process if promo code update fails
          }
        }
        
        // Pass all required parameters to the callback
        onPaymentComplete(
          response.paymentId!, 
          response.status || 'COMPLETED', 
          'square'
        );
      } catch (createCustomerError) {
        console.error('Error creating customer or processing payment:', createCustomerError);
        
        // Attempt payment without customer creation if that fails
        const response = await createSquarePayment(
          result.token,
          finalPaymentAmount,
          'USD',
          'Marina reservation'
        );
        
        if (!response.success) {
          throw new Error(response.error || 'Payment processing failed');
        }
        
        console.log('Payment succeeded (without customer):', response);
        
        // If a promo code was used, increment its usage count
        if (promoCodeApplied && promoCodeDetails?.id) {
          try {
            await applyPromoCode(promoCodeDetails.id);
            console.log(`Promo code ${promoCode} applied successfully`);
          } catch (err) {
            console.error('Error applying promo code:', err);
            // Don't stop payment process if promo code update fails
          }
        }
        
        // Pass all required parameters to the callback
        onPaymentComplete(
          response.paymentId!, 
          response.status || 'COMPLETED', 
          'square'
        );
      }
    } catch (err) {
      console.error('Square payment error:', err);
      // Display error in the form without redirecting
      setError(err.message || 'Square payment failed. Please check your card details and try again.');
      // Call the onPaymentError callback to notify parent components but stay on payment page
      onPaymentError(err.message || 'Square payment failed');
      // Reset the form to allow the user to try again
      if (squarePaymentForm) {
        try {
          await squarePaymentForm.destroy();
          // Reinitialize the payment form for a fresh attempt
          if (window.Square && settings) {
            const payments = window.Square.payments(settings.squareApplicationId, settings.squareLocationId);
            const card = await payments.card();
            const container = document.getElementById(SQUARE_CARD_CONTAINER_ID);
            if (container) {
              container.innerHTML = '';
              await card.attach(`#${SQUARE_CARD_CONTAINER_ID}`);
              setSquarePaymentForm(card);
            }
          }
        } catch (resetError) {
          console.error('Error resetting payment form:', resetError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Modify the interval in useEffect to prevent flickering but still ensure correctness
  useEffect(() => {
    // Only run this if we're in deposit mode and we have settings
    if (!settings?.reservationRules?.depositRequired) return;
    
    try {
      const reservationDataStr = localStorage.getItem('reservationData');
      if (!reservationDataStr) return;
      
      const reservationData = JSON.parse(reservationDataStr);
      const depositAmount = getDepositAmount(settings, reservationData);
      
      if (depositAmount === null) return;
      
      // Store the correct final price
      const finalPrice = reservationData.finalPrice;
      
      // Set values ONCE rather than repeatedly with an interval
      setDisplayAmount(depositAmount);
      setPaymentAmount(depositAmount);
      setIsDepositPayment(true);
      
      // If totalPrice isn't already set, set it now
      if (!totalPrice) {
        setTotalPrice(finalPrice);
      }
      
      // We don't need the interval that causes flickering
      // and conflict with React's virtual DOM
    } catch (error) {
      console.error('Error setting up deposit amount:', error);
    }
  }, [settings, totalPrice]);

  // Utility function to capitalize each word
  const capitalizeWords = (str: string) =>
    str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

  if (!settings?.activePaymentMethods?.square && !loading) {
    console.log('Payment processor not configured:', {
      hasSettings: !!settings,
      activePaymentMethods: settings?.activePaymentMethods
    });
    return (
      <Box>
        <Alert severity="error">
          Payment processor is not configured or active. Please contact the marina for assistance.
        </Alert>
      </Box>
    );
  }

  // Log the current payment amount to help debug
  console.log('Rendering payment form:', {
    displayAmount,
    type: typeof displayAmount,
    isDepositPayment,
    totalPrice,
    squareLoaded,
    hasPaymentForm: !!squarePaymentForm,
    containerExists: !!document.getElementById(SQUARE_CARD_CONTAINER_ID),
    containerHTML: document.getElementById(SQUARE_CARD_CONTAINER_ID)?.innerHTML
  });

  // Handle $0 payments (100% discount) - check for CLOSE to zero to handle floating point issues
  if (displayAmount === 0 || displayAmount < 0.01) {
    console.log('Rendering zero-dollar payment form');
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Payment Information
        </Typography>
        <Alert severity="success">
          No payment required. Your reservation will be confirmed automatically.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            // If a promo code was used, increment its usage count
            if (promoCodeApplied && promoCodeDetails?.id) {
              try {
                await applyPromoCode(promoCodeDetails.id);
                console.log(`Promo code ${promoCode} applied successfully for zero-dollar reservation`);
              } catch (err) {
                console.error('Error applying promo code for zero-dollar reservation:', err);
                // Don't stop reservation process if promo code update fails
              }
            }
            onPaymentComplete('free-' + Date.now(), 'COMPLETED', 'none');
          }}
          disabled={disabled}
          sx={{ mt: 2 }}
        >
          {disabled ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              Processing Reservation...
            </>
          ) : (
            'Complete Reservation'
          )}
        </Button>
      </Box>
    );
  }

  console.log('Rendering standard payment form with amount:', displayAmount);
  return (
    <Box>
      <Paper elevation={1} sx={{ p: { xs: 0.5, md: 1 }, width: '100%' }}>
        <Typography variant="subtitle2" gutterBottom>
          Payment
        </Typography>
        {/* Deposit alert: full width, below title/stepper, above main content */}
        {settings?.reservationRules?.depositRequired && (
          <Alert 
            severity="info" 
            icon={<InfoOutlined />}
            sx={{ mb: 1, backgroundColor: 'rgb(229, 246, 253)', color: 'rgb(1, 67, 97)' }}
          >
            <AlertTitle sx={{ fontSize: '1rem', mb: 0.5 }}>Deposit Required</AlertTitle>
            <Typography variant="body2" sx={{ fontSize: '0.95rem', mb: 0.25 }}>
              A deposit of ${settings.reservationRules.depositType === 'fixed' ? 
                settings.reservationRules.depositAmount.toFixed(2) :
                (totalPrice * (settings.reservationRules.depositAmount / 100)).toFixed(2)} will be charged now. The remaining balance of ${(
                  totalPrice - (settings.reservationRules.depositType === 'fixed' ? 
                    settings.reservationRules.depositAmount : 
                    totalPrice * (settings.reservationRules.depositAmount / 100))
                ).toFixed(2)} will be due at check-in.
            </Typography>
            <Typography variant="caption" sx={{ mt: 0.25 }}>
              This deposit amount will be applied towards your final total at checkout.
            </Typography>
          </Alert>
        )}
        <Grid container spacing={{ xs: 0.5, md: 3 }}>
          {/* Left: Reservation Summary & Promo Code */}
          <Grid item xs={12} md={6} sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 0.25 }}>
              Reservation Summary
            </Typography>
            <Box sx={{ mb: 1, width: '100%', overflowX: 'auto' }}>
              {reservationData && (
                <>
                  <SummaryRow>
                    <Typography variant="body1">Vessel Type:</Typography>
                    <Typography variant="body1">{reservationData.vesselType ? capitalizeWords(reservationData.vesselType) : customerInfo?.vesselType ? capitalizeWords(customerInfo.vesselType) : 'pontoon'}</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Date:</Typography>
                    <Typography variant="body1">{formatDate(reservationData.date || customerInfo?.date)}</Typography>
                  </SummaryRow>
                  {reservationData.timeSlot && (() => {
                    const { startTime, endTime, duration } = parseTimeSlot(reservationData.timeSlot);
                    return (
                      <>
                        <SummaryRow>
                          <Typography variant="body1">Time:</Typography>
                          <Typography variant="body1">{startTime} - {endTime}</Typography>
                        </SummaryRow>
                        <SummaryRow>
                          <Typography variant="body1">Rental Duration:</Typography>
                          <Typography variant="body1">{duration}</Typography>
                        </SummaryRow>
                      </>
                    );
                  })()}
                  <SummaryRow>
                    <Typography variant="body1">Rental Duration Price:</Typography>
                    <Typography variant="body1">${(reservationData.rentalPrice || reservationData.originalPrice || 0).toFixed(2)}</Typography>
                  </SummaryRow>
                  {reservationData.discountAmount > 0 && (
                    <>
                      <SummaryRow>
                        <Typography variant="body1">Original Price:</Typography>
                        <Typography variant="body1">${(reservationData.originalPrice || 0).toFixed(2)}</Typography>
                      </SummaryRow>
                      <SummaryRow>
                        <Typography variant="body1" color="success.main">Discount ({reservationData.promoCode}):</Typography>
                        <Typography variant="body1" color="success.main">-${(reservationData.discountAmount || 0).toFixed(2)}</Typography>
                      </SummaryRow>
                      <SummaryRow>
                        <Typography variant="body1">Discounted Price:</Typography>
                        <Typography variant="body1">${(reservationData.rentalPrice || 0).toFixed(2)}</Typography>
                      </SummaryRow>
                    </>
                  )}
                  {reservationData.damageWaiverAmount > 0 && (
                    <SummaryRow>
                      <Typography variant="body1">Damage Waiver:</Typography>
                      <Typography variant="body1">${reservationData.damageWaiverAmount.toFixed(2)}</Typography>
                    </SummaryRow>
                  )}
                  <SummaryRow>
                    <Typography variant="body1">Subtotal:</Typography>
                    <Typography variant="body1">${((reservationData.rentalPrice || reservationData.originalPrice || 0) + (reservationData.damageWaiverAmount || 0)).toFixed(2)}</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Tax:</Typography>
                    <Typography variant="body1">${(reservationData.taxAmount || 0).toFixed(2)}</Typography>
                  </SummaryRow>
                  <TotalRow>
                    <Typography variant="body1">Total:</Typography>
                    <Typography variant="body1">${(reservationData.finalPrice || reservationData.totalPrice || 0).toFixed(2)}</Typography>
                  </TotalRow>
                </>
              )}
              {!reservationData && (
                <>
                  <SummaryRow>
                    <Typography variant="body1">Vessel Type:</Typography>
                    <Typography variant="body1">{customerInfo?.vesselType ? capitalizeWords(customerInfo.vesselType) : 'pontoon'}</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Date:</Typography>
                    <Typography variant="body1">{formatDate(customerInfo?.date)}</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Time:</Typography>
                    <Typography variant="body1">{customerInfo?.startTime || '7:00 AM'} - {customerInfo?.endTime || '8:00 AM'}</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Rental Duration:</Typography>
                    <Typography variant="body1">1 hour</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Rental Duration Price:</Typography>
                    <Typography variant="body1">$0.00</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Damage Waiver:</Typography>
                    <Typography variant="body1">$25.00</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Subtotal:</Typography>
                    <Typography variant="body1">$0.00</Typography>
                  </SummaryRow>
                  <SummaryRow>
                    <Typography variant="body1">Tax:</Typography>
                    <Typography variant="body1">$0.00</Typography>
                  </SummaryRow>
                  <TotalRow>
                    <Typography variant="body1">Total:</Typography>
                    <Typography variant="body1">$0.00</Typography>
                  </TotalRow>
                </>
              )}
            </Box>
            {/* Promo Code Section (unchanged, already collapsible) */}
            <Box sx={{ mb: 0.5 }}>
              {(!showPromoBox && !promoCodeApplied) ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowPromoBox(true)}
                  sx={{ textTransform: 'none', pl: 0 }}
                >
                  Have a promo code?
                </Button>
              ) : (
                <>
                  <Typography variant="caption" gutterBottom>
                    Promo Code
                  </Typography>
                  <Collapse in={showPromoBox || promoCodeApplied} timeout="auto" unmountOnExit>
                    {promoCodeApplied ? (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="subtitle2">
                              Promo code "{promoCode}" applied!
                            </Typography>
                            <Typography variant="body2">
                              You saved ${discountAmount.toFixed(2)}
                            </Typography>
                          </Box>
                          <Button 
                            size="small" 
                            onClick={handleClearPromoCode}
                            color="inherit"
                          >
                            Remove
                          </Button>
                        </Box>
                      </Alert>
                    ) : (
                      <Box sx={{ display: 'flex', mt: 1 }}>
                        <TextField 
                          placeholder="Enter promo code" 
                          variant="outlined" 
                          fullWidth
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          error={!!promoCodeError}
                          helperText={promoCodeError}
                          disabled={checkingPromoCode}
                          sx={{ mr: 2 }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handlePromoCodeValidation();
                            }
                          }}
                        />
                        <Button 
                          variant="outlined" 
                          onClick={handlePromoCodeValidation}
                          disabled={checkingPromoCode || !promoCode.trim()}
                          sx={{ minWidth: 100 }}
                        >
                          {checkingPromoCode ? <CircularProgress size={24} /> : 'APPLY'}
                        </Button>
                      </Box>
                    )}
                    {promoCodeError && !promoCodeApplied && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {promoCodeError}
                      </Typography>
                    )}
                  </Collapse>
                </>
              )}
            </Box>
          </Grid>
          {/* Right: Card/Payment Form */}
          <Grid item xs={12} md={6} sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Payment Information
            </Typography>
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight="bold">
                Deposit Amount: ${displayAmount.toFixed(2)}
              </Typography>
              {isDepositPayment && totalPrice > 0 && (
                <Typography variant="caption" color="text.secondary">
                  The remaining balance of ${(totalPrice - displayAmount).toFixed(2)} will be due at check-in
                </Typography>
              )}
            </Box>
            <Box sx={{ my: 0.5, width: '100%', overflowX: 'auto' }}>
              <Typography variant="caption" gutterBottom>
                Card Information
              </Typography>
              <Box 
                id={SQUARE_CARD_CONTAINER_ID}
                sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px',
                  p: 0.5,
                  minHeight: '40px',
                  mb: 1,
                  width: '100%',
                  maxWidth: { xs: '100%', sm: 400 },
                  boxSizing: 'border-box',
                  overflowX: 'auto'
                }}
                ref={(el) => {
                  if (el) {
                    console.log('Square payment form container mounted:', {
                      id: SQUARE_CARD_CONTAINER_ID,
                      element: el,
                      hasSquare: !!window.Square,
                      squareLoaded,
                      squareInitialized: squareInitialized.current
                    });
                  }
                }}
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSquarePayment}
              disabled={loading || !squarePaymentForm || disabled}
              fullWidth
              sx={{ 
                py: 0.75, 
                textTransform: 'uppercase',
                fontSize: '0.95rem'
              }}
            >
              {loading || disabled ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {disabled ? 'Processing Reservation...' : 'Processing...'}
                </>
              ) : (
                isDepositPayment 
                  ? `Pay Deposit $${displayAmount.toFixed(2)}`
                  : `Pay $${displayAmount.toFixed(2)}`
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

// Styled components for the payment page
const StepIcon = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white'
}));

const SummaryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(0.5)
}));

const TotalRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(0.5),
  fontWeight: 'bold'
}));

export default MultiPaymentForm;